import React, { useState, useEffect, useMemo } from 'react';
import './styles/App.css';
import Header from "./components/Header/Header";
import RegistrationDataGrid from "./components/RegistrationDataTable/RegistrationDataGrid";
import OpenSeadragonViewer from "./components/OpenSeadragonViewer/OpenSeadragonViewer";
import CoregModal from "./components/CoregModal/CoregModal";
import CollapsiblePanel from "./components/CollapsiblePanel/CollapsiblePanel";
import SelectedFolderContext from './context/selectedFolderContext';
import { AuthProvider, AuthContext } from './context/authContext';
import ajaxActions from './services/ajaxActions';
import {
  findFirstDermoscopicImage,
  getRegistrationItemCategory,
  hasDermoscopicImages,
  registrationItemHasBounds,
  getCoregEligibleImages,
} from './utils/registrationHelpers';
import { getImageItemId, getMacroscopicImage } from './utils/imageHelpers';
import { COREG_META_KEY, findCoregMovingLayer } from './utils/coregRegistration';
import { mergeLocalCoregIntoFolder, mergeCoregMetaIntoFolder, getCoregFolderKey, loadCoregFromLocalStorage } from './utils/coregLocalStorage';

const DEFAULT_PANEL_STATE = {
    dermoscopy: true,
    macroscopic: false,
    rcm: false,
    registration: true,
};

/** Registration table: full-width bottom panel vs slim right navigation strip. */
const REGISTRATION_LAYOUT = {
    bottom: 'bottom',
    right: 'right',
};

function AppContent() {
    const { isLoggedIn, authReady } = React.useContext(AuthContext);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedDermImage, setSelectedDermImage] = useState(null);
    const [activeTiledOverlays, setActiveTiledOverlays] = useState({}); // Track which ROI overlays are active
    const [roiVisibilities, setRoiVisibilities] = useState({});
    const [selectedRegistrationIndex, setSelectedRegistrationIndex] = useState(null);
    const [panelCollapsed, setPanelCollapsed] = useState(DEFAULT_PANEL_STATE);
    const [registrationLayout, setRegistrationLayout] = useState(REGISTRATION_LAYOUT.right);
    const [showCoregModal, setShowCoregModal] = useState(false);
    const [dermCoregOverlayOpacity, setDermCoregOverlayOpacity] = useState(50);
    const selectedFolderValue = { selectedFolder, setSelectedFolder };

    const registrationData = selectedFolder?.meta?.registrationData;
    const coregState = selectedFolder?.meta?.[COREG_META_KEY];
    const hasCoregForDerm = Boolean(
        coregState
        && selectedDermImage
        && findCoregMovingLayer(coregState, selectedDermImage)
    );
    const macroImage = useMemo(
        () => getMacroscopicImage(selectedFolder),
        [selectedFolder]
    );
    const coregEligibleImages = useMemo(
        () => getCoregEligibleImages(registrationData),
        [registrationData]
    );
    const canOpenCoreg = Boolean(
        selectedFolder
        && coregEligibleImages.macroscopic.length > 0
        && coregEligibleImages.dermoscopic.length > 0
    );
    const showDermoscopyPanel = useMemo(
        () => hasDermoscopicImages(registrationData),
        [registrationData]
    );

    const notifyViewersResize = () => {
        window.setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            ['MAIN_VIEWER', 'NAV_VIEWER', 'DERM_VIEWER'].forEach((key) => {
                const osdViewer = window[key];
                if (!osdViewer || typeof osdViewer.resize !== 'function') {
                    return;
                }

                const element = osdViewer.element;
                if (!element || element.clientWidth <= 0 || element.clientHeight <= 0) {
                    return;
                }

                try {
                    osdViewer.resize();
                } catch (error) {
                    console.warn(`OpenSeadragon resize skipped for ${key}:`, error);
                }
            });
        }, 220);
    };

    const togglePanel = (panelKey) => {
        setPanelCollapsed((prev) => ({
            ...prev,
            [panelKey]: !prev[panelKey],
        }));

        notifyViewersResize();
    };

    const expandPanel = (panelKey) => {
        setPanelCollapsed((prev) => {
            if (!prev[panelKey]) return prev;
            return { ...prev, [panelKey]: false };
        });
        notifyViewersResize();
    };

    // New folder/case: reset viewer state, merge browser-saved coreg, then server coreg when logged in.
    useEffect(() => {
        setRoiVisibilities({});
        setActiveTiledOverlays({});
        setSelectedImage(null);
        setSelectedRegistrationIndex(null);

        if (!selectedFolder) {
            setSelectedDermImage(null);
            return;
        }

        const folderId = getCoregFolderKey(selectedFolder);
        const localCoreg = folderId ? loadCoregFromLocalStorage(folderId) : null;
        let mergedFolder = mergeLocalCoregIntoFolder(selectedFolder);

        if (mergedFolder !== selectedFolder) {
            setSelectedFolder(mergedFolder);
        }

        const reg = mergedFolder?.meta?.registrationData ?? selectedFolder?.meta?.registrationData;
        const firstDerm = findFirstDermoscopicImage(reg);
        setSelectedDermImage(firstDerm);

        if (!authReady || !isLoggedIn || !folderId || localCoreg || !selectedFolder._id) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const folder = await ajaxActions.getFolder(selectedFolder._id);
                if (cancelled || !folder?.meta?.[COREG_META_KEY]) return;

                const withServerCoreg = mergeCoregMetaIntoFolder(selectedFolder, {
                    [COREG_META_KEY]: folder.meta[COREG_META_KEY],
                    registrationData: folder.meta.registrationData,
                });

                if (withServerCoreg !== selectedFolder) {
                    setSelectedFolder(withServerCoreg);
                    const serverReg = withServerCoreg.meta?.registrationData;
                    const serverDerm = findFirstDermoscopicImage(serverReg);
                    if (serverDerm) {
                        setSelectedDermImage(serverDerm);
                    }
                }
            } catch (error) {
                console.warn('Could not load saved co-registration from server:', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedFolder?._id, authReady, isLoggedIn]);

    // Re-measure OpenSeadragon viewers when panel layout changes (e.g. registration collapsed)
    useEffect(() => {
        const resizeAllViewers = () => {
            ['MAIN_VIEWER', 'NAV_VIEWER', 'DERM_VIEWER'].forEach((key) => {
                const osdViewer = window[key];
                if (!osdViewer?.element || typeof osdViewer.resize !== 'function') return;
                const { clientWidth, clientHeight } = osdViewer.element;
                if (clientWidth <= 0 || clientHeight <= 0) return;
                try {
                    osdViewer.resize();
                } catch (error) {
                    console.warn(`OpenSeadragon resize skipped for ${key}:`, error);
                }
            });
        };

        const t1 = window.setTimeout(resizeAllViewers, 0);
        const t2 = window.setTimeout(resizeAllViewers, 250);
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
        };
    }, [panelCollapsed, registrationLayout]);

    const handleRegistrationUpdated = (metaPatch, { dermImage: savedDerm } = {}) => {
        setSelectedFolder((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                meta: {
                    ...prev.meta,
                    ...metaPatch,
                },
            };
        });
        if (savedDerm) {
            setSelectedDermImage(savedDerm);
        }
    };

    // Handle ROI visibility changes
    const handleVisibilityChange = (roiIndex, isVisible) => {
        console.log(`📦 App.js: handleVisibilityChange called for ROI ${roiIndex}:`, isVisible);
        setRoiVisibilities(prev => {
            const newVisibilities = {
                ...prev,
                [roiIndex]: isVisible
            };
            console.log('📦 App.js: Updated roiVisibilities state:', newVisibilities);
            return newVisibilities;
        });
    };

    // Handle image selection from the registration table
    const handleImageSelect = (item, registrationIndex) => {
        if (!item || registrationIndex == null) return;

        setSelectedRegistrationIndex(registrationIndex);

        const category = getRegistrationItemCategory(item);
        console.log('📦 App.js: Image selected:', {
            type: item.type,
            category,
            registrationIndex,
            isic_id: item.isic_id,
        });

        if (category === 'dermoscopic') {
            setSelectedDermImage(item);
            setActiveTiledOverlays({});
            if (panelCollapsed.dermoscopy) {
                expandPanel('dermoscopy');
            }
            if (registrationItemHasBounds(item)) {
                handlePanToROI(registrationIndex, item);
            }
        } else if (category === 'rcm') {
            // Row click loads the stack in the RCM viewer only; macroscopic overlays
            // are toggled separately via the folder icon in the registration table.
            setSelectedImage(item);
            if (panelCollapsed.rcm) {
                expandPanel('rcm');
            }
        } else if (category === 'macroscopic') {
            setActiveTiledOverlays({});
        }
    };

    // Handle overlay toggle (add/remove tiled image overlay on main viewer)
    const handleOverlayToggle = (roiIndex, isActive) => {
        console.log(`📦 App.js: Overlay toggle for ROI ${roiIndex}:`, isActive);

        setActiveTiledOverlays(prev => {
            const newOverlays = { ...prev };
            if (isActive) {
                // Get the registration item for this ROI
                const registrationData = selectedFolder?.meta?.registrationData;
                if (registrationData && Array.isArray(registrationData) && registrationData[roiIndex]) {
                    newOverlays[roiIndex] = registrationData[roiIndex];
                    console.log(`📦 App.js: Activated overlay for ROI ${roiIndex}`);
                }
            } else {
                // Remove this overlay
                delete newOverlays[roiIndex];
                console.log(`📦 App.js: Deactivated overlay for ROI ${roiIndex}`);
            }
            console.log('📦 App.js: Updated activeTiledOverlays:', newOverlays);
            return newOverlays;
        });
    };

    // Handle pan to ROI (center viewport on ROI without changing zoom)
    const handlePanToROI = (roiIndex, data) => {
        console.log(`🎯 App.js: Pan to ROI ${roiIndex}:`, data);

        // Access the main viewer via window.MAIN_VIEWER (exposed by OpenSeadragonViewer)
        const viewer = window.MAIN_VIEWER;
        if (!viewer) {
            console.warn('⚠️  Main viewer not available');
            return;
        }

        // Parse coordinates
        const x_min = parseFloat(data.x_min) || 0;
        const x_max = parseFloat(data.x_max) || 0;
        const y_min = parseFloat(data.y_min) || 0;
        const y_max = parseFloat(data.y_max) || 0;

        // Calculate center of ROI in image coordinates
        const centerX = (x_min + x_max) / 2;
        const centerY = (y_min + y_max) / 2;

        console.log(`🎯 ROI center in image coordinates: (${centerX}, ${centerY})`);

        // Get the base tiled image to convert image coordinates to viewport coordinates
        const tiledImage = viewer.world.getItemAt(0);
        if (!tiledImage) {
            console.warn('⚠️  No image loaded in viewer');
            return;
        }

        // Convert image coordinates to viewport coordinates
        const viewportPoint = tiledImage.imageToViewportCoordinates(centerX, centerY);
        console.log(`🎯 Viewport point:`, viewportPoint);

        // Pan to the center of the ROI without changing zoom
        viewer.viewport.panTo(viewportPoint, true); // true = immediately, no animation

        console.log(`✅ Panned to ROI ${roiIndex}`);
    };

    // Handle coordinate changes (redraw ROIs when coordinates are edited)
    const handleCoordinateChange = (roiIndex, field, newValue) => {
        console.log(`📝 App.js: Coordinate changed for ROI ${roiIndex}:`, { field, newValue });

        // Access the main viewer to redraw ROIs
        const viewer = window.MAIN_VIEWER;
        if (viewer && selectedFolder?.meta?.registrationData) {
            // Trigger ROI redraw by accessing the exposed drawROIsFromRegistrationData function
            // The viewer exposes this via window.MAIN_VIEWER
            console.log('🔄 Redrawing ROIs after coordinate change...');

            // Use a small delay to ensure the data has been updated
            setTimeout(() => {
                // Access the draw function from the window object
                if (window.MAIN_VIEWER_DRAW_ROIS) {
                    window.MAIN_VIEWER_DRAW_ROIS(selectedFolder.meta.registrationData);
                    console.log('✅ ROIs redrawn');
                }
            }, 50);
        }
    };


    return (
            <SelectedFolderContext.Provider value={selectedFolderValue}>
                <div className="App">
                    <header className="App-header">
                        <Header />
                    </header>
                    <main
                        className={`App-main${
                            registrationLayout === REGISTRATION_LAYOUT.bottom
                            && panelCollapsed.registration
                                ? ' App-main--registration-collapsed'
                                : ''
                        }`}
                    >
                        <div className="top-section">
                            {showDermoscopyPanel && (
                                <CollapsiblePanel
                                    title="Dermoscopy"
                                    edge="left"
                                    collapsed={panelCollapsed.dermoscopy}
                                    onToggle={() => togglePanel('dermoscopy')}
                                    className="dermoscopy-panel"
                                >
                                    <div className="dermoscopy-panel-inner">
                                        <div className="dermoscopy-panel-toolbar">
                                            <button
                                                type="button"
                                                className="coreg-open-button"
                                                disabled={!canOpenCoreg}
                                                onClick={() => setShowCoregModal(true)}
                                                title={
                                                    canOpenCoreg
                                                        ? 'Open co-registration tool for macroscopic and dermoscopic images'
                                                        : 'Select a case with macroscopic and dermoscopic images'
                                                }
                                            >
                                                Co-Register
                                            </button>
                                        </div>
                                        <OpenSeadragonViewer
                                            selectedFolder={selectedFolder}
                                            selectedImage={selectedDermImage}
                                            isDermoscopyViewer={true}
                                        />
                                    </div>
                                </CollapsiblePanel>
                            )}
                            <CollapsiblePanel
                                title="Macroscopic"
                                edge="left"
                                collapsed={panelCollapsed.macroscopic}
                                onToggle={() => togglePanel('macroscopic')}
                                className="main-content"
                            >
                                <div className="main-content-inner">
                                    {hasCoregForDerm && (
                                        <label className="macro-coreg-opacity">
                                            <span className="macro-coreg-opacity-label">
                                                Registered dermoscopy overlay
                                            </span>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={dermCoregOverlayOpacity}
                                                onChange={(e) => setDermCoregOverlayOpacity(Number(e.target.value))}
                                            />
                                            <span className="macro-coreg-opacity-value">
                                                {dermCoregOverlayOpacity}%
                                            </span>
                                        </label>
                                    )}
                                    <OpenSeadragonViewer
                                        selectedFolder={selectedFolder}
                                        roiVisibilities={roiVisibilities}
                                        activeTiledOverlays={activeTiledOverlays}
                                        selectedRegistrationIndex={selectedRegistrationIndex}
                                        dermCoregImage={hasCoregForDerm ? selectedDermImage : null}
                                        coregState={hasCoregForDerm ? coregState : null}
                                        dermCoregOpacity={hasCoregForDerm ? dermCoregOverlayOpacity / 100 : 0}
                                    />
                                </div>
                            </CollapsiblePanel>
                            <CollapsiblePanel
                                title="RCM Stack"
                                edge="right"
                                collapsed={panelCollapsed.rcm}
                                onToggle={() => togglePanel('rcm')}
                                className="navigation-panel"
                            >
                                <div className="navigation-panel-inner">
                                    <OpenSeadragonViewer
                                        selectedFolder={selectedFolder}
                                        selectedImage={selectedImage}
                                        isNavigationViewer={true}
                                    />
                                </div>
                            </CollapsiblePanel>
                            {registrationLayout === REGISTRATION_LAYOUT.right && (
                                <CollapsiblePanel
                                    title="Registration"
                                    edge="right"
                                    collapsed={panelCollapsed.registration}
                                    onToggle={() => togglePanel('registration')}
                                    className="registration-section registration-section--nav"
                                >
                                    <RegistrationDataGrid
                                        selectedFolder={selectedFolder}
                                        selectedRegistrationIndex={selectedRegistrationIndex}
                                        onImageSelect={handleImageSelect}
                                        onVisibilityChange={handleVisibilityChange}
                                        onOverlayToggle={handleOverlayToggle}
                                        onPanToROI={handlePanToROI}
                                        onCoordinateChange={handleCoordinateChange}
                                        layoutMode="navigation"
                                        registrationLayout={registrationLayout}
                                        onRegistrationLayoutChange={setRegistrationLayout}
                                    />
                                </CollapsiblePanel>
                            )}
                        </div>
                        {registrationLayout === REGISTRATION_LAYOUT.bottom && (
                            <CollapsiblePanel
                                title="Registration Data"
                                edge="bottom"
                                collapsed={panelCollapsed.registration}
                                onToggle={() => togglePanel('registration')}
                                className="registration-section"
                            >
                                <RegistrationDataGrid
                                    selectedFolder={selectedFolder}
                                    selectedRegistrationIndex={selectedRegistrationIndex}
                                    onImageSelect={handleImageSelect}
                                    onVisibilityChange={handleVisibilityChange}
                                    onOverlayToggle={handleOverlayToggle}
                                    onPanToROI={handlePanToROI}
                                    onCoordinateChange={handleCoordinateChange}
                                    layoutMode="full"
                                    registrationLayout={registrationLayout}
                                    onRegistrationLayoutChange={setRegistrationLayout}
                                />
                            </CollapsiblePanel>
                        )}
                    </main>
                    <CoregModal
                        isOpen={showCoregModal}
                        onClose={() => setShowCoregModal(false)}
                        selectedFolder={selectedFolder}
                        macroImage={macroImage}
                        dermImage={selectedDermImage}
                        onRegistrationUpdated={handleRegistrationUpdated}
                    />
                </div>
            </SelectedFolderContext.Provider>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
